import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { ActionService } from './action.service';
import { ActionDTO, ActionTypeDTO, LinkedActionsDTO } from '../api/plyca-dtos';
import {
  Component,
  Input,
  Output,
  OnInit,
  EventEmitter,
  ComponentFactoryResolver,
  OnDestroy
} from '@angular/core';

import { ServiceLocator } from '../ServiceLocator';
import { TranslateService } from '@ngx-translate/core';
import { ActionsProcessComponent } from './actions.process.component';
import { WaitLoadingService } from '../components/common/waitLoading/wait-loading.service';
import { PlycaDataService } from '../../services/plyca.service';
import { PropertyService } from '../properties/property.service';
import { BaseComponent } from '../components/common/baseComponent/base.component';
import { MenuItem } from 'primeng-lts/api';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { CHANGE_STATUS_SITUATION } from '../dossiers/dossierActions/change.situation.component';
import { SET_VARIABLES } from '../dossiers/dossierActions/setVariables.component';
import { Observable, of, from, EMPTY } from 'rxjs';
import * as lodash from 'lodash';
import { TenderClassificationComponent } from '../tasks/tenderClassification/component/tenderClassification.component';
import { ProcurementProjectDTO } from '../api/plyca-dtos-v2';
import { ProcurementProjectLotStateInfoComponent } from '../tasks/procurementProjectLotStateInfo/component/procurementProjectLotStateInfo.component';


@Component({
  selector: 'pmenu-actions',
  templateUrl: 'pmenu.actions.template.html'
})
export class PMenuActionsComponent extends BaseComponent implements OnInit, OnDestroy {


  @Input()
  dto: any;
  @Input()
  public lazy = false;
  @Input()
  items: MenuItem[] = [];
  @Input()
  styleClass = '';
  @Input()
  public oneButton = false;
  @Input()
  public dtoType: string;
  @Input()
  identifier: any;
  @Output()
  reload = new EventEmitter<any>();
  /**
   * Permite modificar el listado de acciones final
   *
   * Para ello:
   * 1. Definir en el template del componente que incluye el menú de acciones la función de "post-procesado".
   * Esta función recibe una lista de objetos de tipo MenuItem[], que contiene la lista de acciones asociadas al recurso,
   *  y deberá devolver un objeto de tipo Observable<MenuItem[]> con la lista final de acciones modificada.
   *
   * Los objetos MenuItem consultados al servidor tendrán las en la propiedad state de cada objeto MenuItem las siguientes propiedades:
   * 1. action: Contiene los datos de la acción a ejecutar
   * 2. dto: Contiene los datos del objeto sobre el que se debe ejecutar la acción
   *
   * Ejemplo: Ver documentsComponent.updateCorrectDocumentAction
   *
   * 2.Pasar la función anteriormente implementada al componente de menú de acciones. Por ejemplo: Ver documents.template.html
   */
  @Input()
  public postProcessMenuActionsFn: (items: MenuItem[]) => Observable<MenuItem[]>;

  private translateService: TranslateService;
  private actionService: ActionService;

  public actionButton: MenuItem;
  public tooltip = 'No hay acciones disponibles';
  public options: MenuItem[] = [];
  private loadedLazyActions = false;
  public isDisabled = '';
  private propertyService: PropertyService;

  constructor(private dialogService: DialogService,
    public ref: DynamicDialogRef,
    public waitService: WaitLoadingService,
    public plycaService: PlycaDataService,
    protected componentFactoryResolver: ComponentFactoryResolver
  ) {
    super();
    this.translateService = ServiceLocator.injector.get(TranslateService);
    this.actionService = ServiceLocator.injector.get(ActionService);
    this.propertyService = ServiceLocator.injector.get(PropertyService);
  }

  public getDTO(identifier: any) {
    this.plycaService.getProcurementProject(identifier, '?expand=expansionSet:minimal').pipe(
      tap((project: ProcurementProjectDTO) => {
        this.dto = project;
      }),
      catchError((error: any) => {
        console.error('Error al obtener el proyecto:', error);
        // Puedes realizar acciones adicionales aquí, como mostrar un mensaje de error al usuario
        return undefined; // Relanza el error para que el componente padre pueda manejarlo
      })
    ).subscribe(
      (data: ProcurementProjectDTO) => {
        // Manejar los datos aquí
        console.log('Datos del proyecto:', data);
        // Realizar más acciones con los datos recibidos, si es necesario
      }
    );
  }


  ngOnInit(): void {
    this.styleClass = 'btn btn-outline-secondary btn-xs t-action-button' + this.styleClass;
    this.options = [].concat(this.items);

    if (this.dto === undefined && this.identifier !== null || this.identifier !== undefined) {
      this.getDTO(this.identifier);
    }
    if (!this.lazy) {
      if (this.dto.availableActions && this.dto.availableActions.actions) {
        this.subscribe(this.loadMenuOptions(this.dto.availableActions.actions));
      }
      if (this.oneButton) {
        if (this.dto.availableActions && this.dto.availableActions.actions) {
          let action = this.dto.availableActions.actions[0];
          this.actionButton = <MenuItem>{};
          this.actionButton.label = action.name;
          this.actionButton.title = action.description;
          this.actionButton.icon = this.actionService.getActionIconClass(action);
          this.actionButton.command = () => {
            this.subscribe(this.openModal(action, this.dto).onClose,
              (() => this.reloadEvent())
            );
          };
        }
      }
    }
  }
  private loadMenuOptions(actions: ActionDTO[]): Observable<MenuItem[]> {
    let serverActions = new Array<MenuItem>();
    let obs: Observable<MenuItem[]>;
    if (actions) {
      obs = from(actions).pipe(
        mergeMap((action: ActionDTO) => this.checkVisibility(action)),
        map((action: ActionDTO) => {
          if (action.visible) {
            return this.getMenuItem(action);
          } else {
            console.log('La acción ' + action.identifier + ' se ignora por la configuración de visibilidad del cliente');
            return undefined;
          }
        }),
        tap((item: MenuItem) => {
          if (item) {
            serverActions.push(item);
          }
        }),
        map(() => serverActions),
      );
    } else {
      obs = EMPTY;
    }
    return obs.pipe(
      finalize(() => {
        // ordenar acciones de servidor
        let finalList: Array<MenuItem>;
        if (this.items && this.items.length > 0) {
          finalList = this.items;
          finalList.push({ separator: true });
        } else {
          finalList = [];
        }
        finalList.push(...this.sortActions(serverActions));
        this.subscribe(this.postProcessMenuActions(finalList).pipe(
          tap((result: MenuItem[]) => this.options = result)
        ));
      })
    );
  }
  private sortActions(actions: MenuItem[]): MenuItem[] {
    return (actions) ? lodash.sortBy(actions, ['label']) : actions;
  }

  private postProcessMenuActions(lista: MenuItem[]): Observable<MenuItem[]> {
    return (this.postProcessMenuActionsFn) ? this.postProcessMenuActionsFn(lista) : of(lista);
  }

  private checkVisibility(action: ActionDTO): Observable<any> {
    // La propiedad es de adaptacion, puede o no existir
    return this.propertyService.getProperty('action.' + action.identifier + '.visible')
      .pipe(
        tap((value: string) => {
          this.setVisibilityValue(value, action);
        }),
        catchError(() => {
          this.setVisibilityValue(null, action);
          return of(action)
        })
      )
  }

  private setVisibilityValue(value: string, action: ActionDTO): void {
    let actionVisible = false;
    if (value == null || value === '') {
      actionVisible = action.visible;
    } else {
      actionVisible = value === 'true';
    }
    if (actionVisible == null || actionVisible === undefined) {
      actionVisible = true;
    }
    action.visible = actionVisible;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public getAvailableActions() {
    if (this.lazy && !this.loadedLazyActions && this.isDisabled !== 'disabled') {
      this.isDisabled = 'disabled';
      this.subscribe(this.plycaService.getAvailableActions(this.dtoType, this.dto.identifier).pipe(
        switchMap((actions: LinkedActionsDTO) => this.loadMenuOptions(actions?.actions)),
        finalize(() => {
          this.loadedLazyActions = true;
          this.isDisabled = '';
        })
      )
      );
    }
  }

  public reloadEvent() {
    this.reload.emit(this.dto);
  }

  public getMenuItem(action: ActionDTO): MenuItem {
    let mn: MenuItem = {};
    mn = <MenuItem>{};
    mn.label = action.name;
    mn.title = action.description;
    mn.icon = action.icon ? action.icon : this.actionService.getActionIconClass(action);
    mn.state = {
      action: action,
      dto: this.dto
    }
    let c = undefined
    if (action.identifier === ActionTypeDTO.CHANGE_STATUS_SITUATION) {
      c = CHANGE_STATUS_SITUATION;
    } else if (action.identifier === ActionTypeDTO.SET_TASK_VARIABLES || action.identifier === ActionTypeDTO.SET_PROCEDURE_VARIABLES) {
      c = SET_VARIABLES;
    } else if (action.identifier === ActionTypeDTO.OPEN_TENDERCLASSIFICATION) { // Abrimos la pantalla de clasificación de ofertas. (AYMAD)
      c = TenderClassificationComponent;
    } else if (action.identifier === ActionTypeDTO.OPEN_PROCUREMENTPROJECTLOTSTATEINFO) { // Abrimos la pantalla de estado actual de lotes de un expediente de contratación. (AYMAD)
      c = ProcurementProjectLotStateInfoComponent;
    }

    mn.command = () => {
      this.subscribe(
        this.openModal(action, this.dto, c).onClose, (() => this.reloadEvent()));
    };
    return mn;
  }

  public executeButtonCommand() {
    this.subscribe(this.openModal(this.dto.availableActions.actions[0], this.dto).onClose, (() => this.reloadEvent()));
  }

  openModal(action: any, dto: any, component = ActionsProcessComponent) {
    let headerTitle = action.name + ': ' + (dto.description ? dto.description : dto.name);
    headerTitle = headerTitle.substr(0, 100) + '...';
    let config: DynamicDialogConfig = {
      header: headerTitle,
      width: '70%',
      styleClass: 'actionStyle',
      style: { overflow: 'hidden' }
    };
    config['action'] = action;
    config['paramsDef'] = action.definition.parametersDefinition;
    config['dto'] = dto;
    return this.dialogService.open(component, config);
  }

  rebindMenuItems(newItems: MenuItem[]) {
    let lista = [].concat(newItems);
    this.items = lista;
  }

  noActions() {
    this.options = [];
    let mn: MenuItem = {};
    mn = <MenuItem>{};
    mn.label = this.translateService.instant('Actions.empty');
    mn.title = '';
    mn.icon = '';
    this.options.push(mn);
  }

  getTooltip() {
    return this.tooltip;
  }

}
